import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Form } from 'informed';
import { Button as RMDButton, CircularProgress } from 'react-md';
import { withStyles } from '@material-ui/core/styles';
import { compact, debounce, get } from 'lodash-es';

import Duplicate from '../CommonV2/Duplicate';
import HiddenField from '../Common/HiddenField';
import CoveredBlock from './Covered/CoveredBlock';
import DebugBlock from './Debug/DebugBlock';
import ExtraBlock from './Extra/ExtraBlock';
import LoanBlock from './Loan/LoanBlock';
import OfferBlock from './Offer/OfferBlock';
import SubscriptionsBlock from './Subscriptions/SubscriptionsBlock';
import TransformBlock from './Transform/TransformBlock';

import withRoot from './withRoot';

import './SchemaForm.scss';

// import { strings as defaultStrings } from './default';

class SchemaForm extends Component {
  constructor(props) {
    super(props);
    const { expanded } = props;
    this.state = {
      expanded: {
        extra: true,
        loan: true,
        covered: true,
        transform: true,
        offer: true,
        subscriptions: false,
        all: true,
        ...expanded,
      },
    };
  }

  setExpanded = (block, status = false) => {
    this.setState(prevState => ({
      expanded: Object.assign(prevState.expanded, { [block]: status }),
    }));
  };

  toggle = block => {
    this.setState(prevState => ({
      expanded: Object.assign(prevState.expanded, { [block]: !prevState.expanded }),
    }));
  };

  setExpandedForAll = status => {
    const { expanded } = this.state;
    Object.keys(expanded).forEach(key => {
      this.setExpanded(key, status);
    });
  };

  handleSave = (...values) => {
    const { onSave } = this.props;
    onSave(...values);
  };

  handleCalculate = (...values) => {
    const { onCalculate } = this.props;
    this.setState({
      expanded: {
        extra: false,
        loan: false,
        covered: false,
        transform: true,
        subscriptions: true,
        offer: true,
        all: false,
      },
    });
    onCalculate(...values);
  };

  handleSubscribe = (...values) => {
    const { onSubscribes } = this.props;
    this.setState({
      expanded: {
        extra: false,
        loan: false,
        covered: false,
        transform: true,
        offer: false,
        all: false,
      },
    });
    onSubscribes(...values);
  };

  handleSubscribes = (...values) => {
    const { onSubscribes } = this.props;
    this.setState({
      expanded: {
        extra: false,
        loan: false,
        covered: false,
        transform: true,
        offer: false,
        all: false,
      },
    });
    onSubscribes(...values);
  };

  handlePutOnHold = (...values) => {
    const { onPutOnHold } = this.props;
    onPutOnHold(...values);
  };

  handlePrint = (...values) => {
    const { onPrint } = this.props;
    onPrint(...values);
  };

  handleCancel = (...values) => {
    const { onCancel } = this.props;
    onCancel(...values);
  };

  render() {
    const { actions, onChange, onSave, classes, validate, i18n, duplicateBtn, ...props } = this.props;
    const { expanded } = this.state;
    const {
      isLoan,
      id,
      initialValues,
      config,
      loading,
      data,
      actionsState,
      readOnly,
      strings,
      apiBaseUrl,
      singleCovered,
      defaultValues,
    } = props;

    const loadingBlock = () =>
      loading.isSubmittingQuotation || loading.isSubmittingQuotations ? (
        <div className="global-loading">
          <CircularProgress id="loading-anim-offer" />
        </div>
      ) : undefined;

    // Need debounce because handleChange was called 2 times
    // First time with old values
    // Second time with new values
    // No field in values could differentiate the two
    const debouncedHandleChange = debounce(formState => {
      let subvalues = get(formState, 'values');
      subvalues = { ...subvalues, subscriptions: compact(get(subvalues, 'subscriptions', [])) };
      const cleaned = { ...formState, values: subvalues };
      onChange(cleaned);
    }, 20);

    const handleChange = formState => debouncedHandleChange(formState);

    const loanBlock = ({ formApi, formState }) => {
      if (isLoan)
        return (
          <LoanBlock
            formApi={formApi}
            formState={formState}
            data={data}
            onSave={onSave}
            strings={strings}
            classes={classes}
            config={config}
            values={formState.values}
            actions={actions}
            expanded={get(expanded, 'loan')}
            setExpanded={this.setExpanded}
            readOnly={readOnly}
            releaseDate={initialValues.date}
          />
        );
      return '';
    };

    const offerBlock = ({ formApi, formState }) => {
      // display offer if exist
      const { offer } = initialValues;

      return (
        <OfferBlock
          data={offer ? offer.data : []}
          columns={offer ? offer.columns : []}
          formApi={formApi}
          formState={formState}
          dataCalc={data}
          loading={loading.isCalculatingQuotation}
          strings={strings}
          classes={classes}
          expanded={get(expanded, 'offer')}
          setExpanded={this.setExpanded}
          onCalculate={this.handleCalculate}
          onPrint={this.handlePrint}
          isLoan={isLoan}
          readOnly={readOnly}
          disabled={formApi.pristine || formApi.dirty}
          actionsState={actionsState}
          validate={validate}
          formalities={offer?.formalities}
          offer={offer}
        />
      );
    };

    const coveredBlock = ({ formApi, formState }) => {
      const values = get(formState, 'values');

      return (
        <CoveredBlock
          formApi={formApi}
          formState={formState}
          data={data}
          onSave={onSave}
          strings={strings}
          classes={classes}
          config={config}
          values={values}
          actions={actions}
          expanded={get(expanded, 'covered')}
          setExpanded={this.setExpanded}
          isLoan={isLoan}
          readOnly={readOnly}
          i18n={i18n}
          singleCovered={singleCovered}
        />
      );
    };

    const transformBlock = ({ formApi, formState }) => (
      <TransformBlock
        key="TransformBlock"
        formApi={formApi}
        formState={formState}
        data={data}
        onSave={onSave}
        config={config}
        strings={strings}
        classes={classes}
        actions={actions}
        expanded={get(expanded, 'transform')}
        setExpanded={this.setExpanded}
        values={formState.values}
        onSubscribe={this.handleSubscribe}
        onPutOnHold={this.handlePutOnHold}
        onPrint={this.handlePrint}
        onCancel={this.handleCancel}
        actionsState={actionsState}
        readOnly={readOnly}
        apiBaseUrl={apiBaseUrl}
        validate={validate}
        defaultValues={defaultValues}
      />
    );

    const subscriptionsBlock = ({ formApi, formState }) => (
      <SubscriptionsBlock
        key="SubscriptionsBlock"
        formApi={formApi}
        formState={formState}
        data={data}
        onSave={onSave}
        config={config}
        strings={strings}
        classes={classes}
        actions={actions}
        expanded={get(expanded, 'subscriptions')}
        setExpanded={this.setExpanded}
        values={formState.values}
        onSubscribe={this.handleSubscribes}
        onPutOnHold={this.handlePutOnHold}
        onPrint={this.handlePrint}
        onCancel={this.handleCancel}
        actionsState={actionsState}
        readOnly={readOnly}
        apiBaseUrl={apiBaseUrl}
        validate={validate}
        defaultValues={defaultValues}
      />
    );

    const extraBlock = ({ formApi, formState }) => (
      <ExtraBlock
        formApi={formApi}
        formState={formState}
        data={data}
        onSave={onSave}
        strings={strings}
        classes={classes}
        expanded={expanded.extra}
        setExpanded={this.setExpanded}
        values={formState.values}
        readOnly={readOnly}
        i18n={i18n}
      />
    );

    const defaultRootBlock = () => (
      <>
        <HiddenField field="id" initialValue={id} />
      </>
    );

    const debugBlock = formState => {
      const { debug } = this.props;
      return (
        <DebugBlock values={formState.values} touched={formState.touched} initialValues={initialValues} debug={debug} />
      );
    };

    const actionsBlock = formApi => (
      <div className="actions-block" style={{ display: 'flex' }}>
        {/* <div className="left">
          <RMDButton
            flat
            primary
            iconBefore={false}
            swapTheming
            iconChildren="euro_symbol"
            disabled={formApi.pristine || formApi.dirty}
            onClick={() => this.handleCalculate(formApi.getState().values)}
          >
            {strings['btn.calculate.label']}
          </RMDButton>
        </div> */}
        <div className="right">
          <Duplicate
            label={get(strings, 'btn.duplicate.label', 'Dupliquer')}
            validateLabel={get(strings, 'btn.duplicate.validate.label', 'Valider')}
            cancelLabel={get(strings, 'btn.duplicate.cancel.label', 'Annuler')}
            isLoadingValidate={duplicateBtn?.isLoadingValidate}
            selection={duplicateBtn?.selection}
            eventEmitter={duplicateBtn?.handle}
            styleSelect={{ width: '310px', minWidthMenu: null, height: '32px' }}
          />
          <RMDButton
            raised
            primary
            iconBefore={false}
            swapTheming
            iconChildren={expanded.all ? 'expand_less' : 'expand_more'}
            disabled={formApi.pristine || formApi.dirty}
            onClick={() => this.setExpandedForAll(!expanded.all)}
          >
            {expanded.all ? strings['btn.collapse.label'] : strings['btn.expand.label']}
          </RMDButton>
        </div>
      </div>
    );
    try {
      return (
        <div className="SchemaForm">
          <Form id="state-form" onChange={handleChange} initialValues={initialValues}>
            {({ formState, formApi }) => (
              <>
                {actionsBlock(formApi)}
                <div className="form-block-container">
                  <div className={classes.root}>
                    {loanBlock({ formApi, formState })}
                    {coveredBlock({ formApi, formState })}
                    {extraBlock({ formApi, formState })}
                    {offerBlock({ formApi, formState })}
                    {isLoan ? subscriptionsBlock({ formApi, formState }) : transformBlock({ formApi, formState })}
                    {defaultRootBlock()}
                  </div>
                </div>
                {debugBlock(formState)}
              </>
            )}
          </Form>
          {loadingBlock()}
        </div>
      );
    } catch (err) {
      console.error('💡 SchemaForm crashed', err);
      return <div>Form crashed</div>;
    }
  }
}

const extraType = PropTypes.arrayOf(
  PropTypes.shape({
    selection: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    name: PropTypes.string,
    code: PropTypes.string,
    sorted: PropTypes.bool,
    default: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  })
);

SchemaForm.propTypes = {
  debug: PropTypes.bool,
  readOnly: PropTypes.bool,
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.shape({ extra: extraType, covered: PropTypes.object, loan: PropTypes.object }),
  initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onCalculate: PropTypes.func,
  onSubscribe: PropTypes.func,
  onSubscribes: PropTypes.func,
  onPutOnHold: PropTypes.func,
  onPrint: PropTypes.func,
  onCancel: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string).isRequired,
  isLoan: PropTypes.bool,
  isMulti: PropTypes.bool,
  config: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  actions: PropTypes.shape({
    fetchLenders: PropTypes.func,
    fetchLenderAddresses: PropTypes.func,
    logout: PropTypes.func,
    validateIban: PropTypes.func,
  }),
  expanded: PropTypes.shape({
    extra: PropTypes.bool,
    loan: PropTypes.bool,
    covered: PropTypes.bool,
  }),
  alerts: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  id: PropTypes.string.isRequired,
  loading: PropTypes.shape({
    isCalculatingQuotation: PropTypes.bool,
    isSavingQuotation: PropTypes.bool,
    isSubmittingQuotation: PropTypes.bool,
    isSubmittingQuotations: PropTypes.bool,
  }),
  actionsState: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  validate: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  apiBaseUrl: PropTypes.string,
  i18n: PropTypes.shape({
    lang: PropTypes.string,
  }).isRequired,
  defaultValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  duplicateBtn: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  formalities: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
};

SchemaForm.defaultProps = {
  debug: false,
  readOnly: false,
  data: { extra: [] },
  initialValues: {},
  onChange: formState => console.log('onChange::', formState), // eslint-disable-line no-console
  onSave: formState => console.log('onSave::', formState), // eslint-disable-line no-console
  onCalculate: formState => console.log('onCalculate::', formState), // eslint-disable-line no-console
  onSubscribe: formState => console.log('onSubscribe::', formState), // eslint-disable-line no-console
  onSubscribes: formState => console.log('onSubscribes::', formState), // eslint-disable-line no-console
  onPutOnHold: formState => console.log('onPutOnHold::', formState), // eslint-disable-line no-console
  onPrint: formState => console.log('onPrint::', formState), // eslint-disable-line no-console
  onCancel: formState => console.log('onCancel::', formState), // eslint-disable-line no-console
  isLoan: false,
  isMulti: false,
  config: {},
  actions: {
    fetchLenders: () => {
      console.log('actions.fetchLenders'); // eslint-disable-line no-console
    },
    fetchLenderAddresses: id => {
      console.log('actions.fetchLenderAddresses::', id); // eslint-disable-line no-console
    },
    logout: formState => console.log('actions.logout::', formState), // eslint-disable-line no-console
    validateIban: iban => console.log('actions.validateIban::', iban), // eslint-disable-line no-console
  },
  expanded: {
    extra: true,
    loan: true,
    covered: true,
    all: true,
  },
  alerts: {
    errors: ["body.covered[0]: should have required property 'type'"],
    infos: [],
    warnings: [],
    successes: [],
  },
  loading: {
    isCalculatingQuotation: false,
    isSavingQuotation: false,
    isSubmittingQuotation: false,
    isSubmittingQuotations: false,
  },
  actionsState: {
    btn: {
      cancel: {
        disable: true,
      },
      subscribe: {
        disable: true,
      },
      putonhold: {
        disable: true,
      },
      print: {
        disable: true,
      },
      calculated: {
        disable: true,
      },
    },
  },
  apiBaseUrl: '/web',
  validate: {},
  defaultValues: undefined,
  duplicateBtn: { isLoadingValidate: false, selection: [], handle: () => { } },
  formalities: [],
};

const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    margin: theme.spacing.unit,
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing.unit * 2,
  },
  button: {
    margin: theme.spacing.unit,
  },
  input: {
    display: 'none',
  },
});

export default withRoot(withStyles(styles)(SchemaForm));
